import { useEffect, useState, useRef } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, Loader2 } from "lucide-react";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp";

function iconFor(ct) {
  if (ct?.startsWith("image/")) return ImageIcon;
  return FileText;
}

function formatBytes(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function CaseDocuments({ caseId, scope = "claimant" }) {
  const t = useT();
  // scope: "claimant" -> uses /me/cases/{id}/documents, "admin" -> /admin/leads/{id}/documents (read-only listing)
  const listUrl = scope === "admin" ? `/admin/leads/${caseId}/documents` : `/me/cases/${caseId}/documents`;
  const uploadUrl = `/me/cases/${caseId}/documents`;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const doUpload = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      return toast.error(t("docs.too_large"));
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("docs.uploaded"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteDoc = async (doc) => {
    if (!confirm(t("docs.delete_confirm", { filename: doc.original_filename }))) return;
    try {
      await api.delete(`/me/cases/${caseId}/documents/${doc.id}`);
      toast.success(t("docs.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid={`docs-${scope}`}>
      {scope === "claimant" && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-[#11322A]/30 bg-[#FDFBF7] p-4">
          <div>
            <div className="font-display text-sm font-bold text-[#0B1317]">{t("docs.add.title")}</div>
            <div className="mt-0.5 text-xs text-[#5C6B68]">{t("docs.add.body")}</div>
          </div>
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid="doc-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-primary !py-2 text-sm disabled:opacity-50"
            data-testid="doc-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? t("docs.uploading") : t("docs.upload")}
          </button>
        </div>
      )}

      {loading && <div className="py-6 text-center text-sm text-[#5C6B68]">{t("docs.loading")}</div>}
      {!loading && docs.length === 0 && (
        <div className="rounded-lg border border-[#E0E4E2] bg-white p-6 text-center text-sm text-[#5C6B68]" data-testid="docs-empty">
          {t("docs.empty")}
        </div>
      )}
      {!loading && docs.length > 0 && (
        <ul className="space-y-2" data-testid="docs-list">
          {docs.map((d) => {
            const Icon = iconFor(d.content_type);
            return (
              <li key={d.id} className="flex items-center gap-3 rounded-lg border border-[#E0E4E2] bg-white p-3" data-testid={`doc-row-${d.id}`}>
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#FDFBF7] text-[#11322A]">
                  <Icon className="h-4 w-4" />
                </div>
                <button onClick={() => openDoc(d)} className="min-w-0 flex-1 text-left" data-testid={`doc-open-${d.id}`}>
                  <div className="truncate text-sm font-bold text-[#0B1317] hover:text-[#E85D04]">{d.original_filename}</div>
                  <div className="mt-0.5 text-xs text-[#5C6B68]">
                    {formatBytes(d.size)} · {new Date(d.created_at).toLocaleDateString()} · {t("docs.uploaded_by")} {d.uploaded_by_role === "admin" ? t("docs.uploader.team") : d.uploaded_by_name || t("docs.uploader.you")}
                  </div>
                </button>
                <button onClick={() => openDoc(d)} className="inline-flex items-center gap-1 rounded-md border border-[#E0E4E2] px-2 py-1 text-xs font-bold text-[#11322A] hover:bg-[#FDFBF7]" data-testid={`doc-view-${d.id}`}>
                  <Download className="h-3 w-3" /> {t("docs.view")}
                </button>
                {scope === "claimant" && (
                  <button onClick={() => deleteDoc(d)} className="inline-flex items-center rounded-md border border-[#F5B6B0] px-2 py-1 text-xs font-bold text-[#B91C1C] hover:bg-[#FDE8E6]" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
